import { lazy } from 'react';

const Groups = lazy(() => import('Pages/Admin/Groups/Groups'));
const Departments = lazy(() => import('Pages/Admin/Departments/Departments'));
const Services = lazy(() => import('Pages/Admin/Services/Services'));
const ProjectTypes = lazy(() => import('Pages/Admin/ProjectTypes/ProjectTypes'));
const TaxRates = lazy(() => import('Pages/Admin/TaxRates/TaxRates'));
const CompanyHolidays = lazy(() => import('Pages/Admin/CompanyHolidays/CompanyHolidays'));
const StockLocations = lazy(() => import('Pages/StockLocation/Index'));
const Options = lazy(() => import('Pages/Options/Index'));

const Dashboard = lazy(() => import('Pages/Dashboard/Dashboard'));
const Noticeboard = lazy(() => import('Pages/Noticeboard/Noticeboard'));
const Diary = lazy(() => import('Pages/Diary/Diary'));
const Events = lazy(() => import('Pages/Events/Events'));
const Guests = lazy(() => import('Pages/Guests/Guests'));
const Approvals = lazy(() => import('Pages/Holiday/Approvals'));
const MyHolidays = lazy(() => import('Pages/Holiday/MyHolidays'));
const MyAbsence = lazy(() => import('Pages/Absence/MyAbsence'));
const Attendance = lazy(() => import('Pages/Attendance/Attendance'));
const People = lazy(() => import('Pages/People/People'));
const ViewPerson = lazy(() => import('Pages/People/ViewPerson'));
const Time = lazy(() => import('Pages/Time/Time'));
const Customers = lazy(() => import('Pages/Customers/Customers'));
const ViewCustomer = lazy(() => import('Pages/Customers/ViewCustomer'));
const Vehicles = lazy(() => import('Pages/Vehicles/Vehicles'));
const Proposals = lazy(() => import('Pages/Proposals/Proposals'));
const ViewProposal = lazy(() => import('Pages/Proposals/ViewProposal'));
const Templates = lazy(() => import('Pages/Templates/Templates'));
const EditTemplate = lazy(() => import('Pages/Templates/EditTemplate'));
const Projects = lazy(() => import('Pages/Projects/Projects'));
const ViewProject = lazy(() => import('Pages/Projects/ViewProject'));
const ProjectDashboard = lazy(() => import('Pages/ProjectDashboard/Dashboard'));
const ProjectDashboardNewCar = lazy(() => import('Pages/ProjectDashboard/NewCar'));
const CurrentProjectsReport = lazy(() => import('Pages/Projects/Current'));
const Suppliers = lazy(() => import('Pages/Suppliers/Suppliers'));
const ViewSupplier = lazy(() => import('Pages/Suppliers/ViewSupplier'));
const Parts = lazy(() => import('Pages/Parts/Parts'));
const ViewPart = lazy(() => import('Pages/Parts/ViewPart'));
const PurchaseOrders = lazy(() => import('Pages/PurchaseOrders/PurchaseOrders'));
const ViewOrder = lazy(() => import('Pages/PurchaseOrders/ViewOrder'));
const HoldingOrders = lazy(() => import('Pages/HoldingOrders/HoldingOrders'));
const PartsPicker = lazy(() => import('Pages/PartsPicker/PartsPicker'));
const Invoices = lazy(() => import('Pages/Invoices/Invoices'));
const ViewInvoice = lazy(() => import('Pages/Invoices/ViewInvoice'));
const Credits = lazy(() => import('Pages/Credits/Credits'));
const ViewCredit = lazy(() => import('Pages/Credits/ViewCredit'));
const Downloads = lazy(() => import('Pages/Downloads'));
const WorkingPatterns = lazy(() => import('Pages/WorkingPatterns/WorkingPatterns'));

const Profile = lazy(() => import('Pages/Settings/Profile'));
const Avatar = lazy(() => import('Pages/Settings/Avatar'));
const Security = lazy(() => import('Pages/Settings/Security'));
const MyDocuments = lazy(() => import('Pages/Settings/Documents'));
const MyObjectives = lazy(() => import('Pages/Settings/Objectives'));
const PersonalDetails = lazy(() => import('Pages/Settings/PersonalDetails'));

const Schedules = lazy(() => import('Pages/Schedules'));
const ViewSchedule = lazy(() => import('Pages/Schedules/View'));

const ToDo = lazy(() => import('Pages/ToDo'));

const Logout = lazy(() => import('Pages/Auth/Logout'));

const Reports = lazy(() => import('Pages/Reports'));
const CustomReports = lazy(() => import('Pages/Reports/Custom/Index'));

const Logs = lazy(() => import('Pages/Logs'));

const Scheduling = lazy(() => import('Pages/Scheduling'));

const GuestCheckin = lazy(() => import('Pages/GuestCheckin'));

const MeetingRoom = lazy(() => import('Pages/Events/MeetingRoom'));

const Documents = lazy(() => import('Pages/Documents'));
const DocumentsView = lazy(() => import('Pages/Documents/View'));

const Handbook = lazy(() => import('Pages/Handbook'));

const OvertimeApprovals = lazy(() => import('Pages/Time/Overtime/Approvals'));

const AccidentLog = lazy(() => import('Pages/AccidentLog'));
const AccidentLogEdit = lazy(() => import('Pages/AccidentLog/Edit'));

const SuspendedStock = lazy(() => import('Pages/SuspendedStock'));

const OrgCharts = lazy(() => import('Pages/OrgChart'));
const OrgChartsView = lazy(() => import('Pages/OrgChart/View'));

const BelowZeroDiary = lazy(() => import('Pages/BelowZero/Diary'));

const Transactions = lazy(() => import('Pages/Transactions'));

const DeleteItems = lazy(() => import('Pages/DeleteItems'));

const JobSpec = lazy(() => import('Pages/JobSpec/Index'));

const routes = [
    { path: ['/admin', '/admin/groups'], component: Groups, scopes: 'groups.*' },
    { path: '/admin/departments', component: Departments, scopes: 'departments.*' },
    { path: '/admin/services', component: Services, scopes: 'services.*' },
    { path: '/admin/projects/types', component: ProjectTypes, scopes: 'project-types.*' },

    { path: '/stock-locations', component: StockLocations, scopes: 'stock-locations.*' },

    { path: '/admin/tax-rates', component: TaxRates, scopes: 'taxes.*' },
    { path: '/admin/working-patterns', component: WorkingPatterns, scopes: 'working-pattern.*' },
    { path: '/admin/company-holidays', component: CompanyHolidays, scopes: 'company-holidays.*' },

    { path: '/admin/options', component: Options, scopes: 'options.*' },

    { path: '/settings/profile', component: Profile, scopes: false },
    { path: '/settings/security/:force?', component: Security, scopes: false },
    { path: '/settings/avatar', component: Avatar, scopes: false },
    { path: '/settings/personal-details', component: PersonalDetails, scopes: false },
    { path: '/settings/documents', component: MyDocuments, scopes: 'hr.has-documents' },
    { path: '/settings/objectives', component: MyObjectives, scopes: 'hr.has-objectives' },

    { path: '/dashboard', component: Dashboard, scopes: 'dashboard.*' },
    { path: '/noticeboard', component: Noticeboard, scopes: 'noticeboard.*' },
    { path: '/diary/:event?/:date?', component: Diary, scopes: 'diary.*' },
    { path: '/workshop-diary/:event?/:date?', component: Diary, scopes: 'workshop-diary.*' },
    { path: '/events', component: Events, scopes: 'events.*' },
    { path: '/meeting-rooms', component: MeetingRoom, scopes: 'meeting-room.manage' },
    { path: '/guests', component: Guests, scopes: 'guests.*' },
    { path: '/holidays', component: Approvals, scopes: ['holiday.review', 'holiday.manage-department'] },
    { path: '/my-holidays', component: MyHolidays, scopes: 'holiday.create' },
    { path: '/my-absences', component: MyAbsence, scopes: 'absence.create' },
    { path: '/attendances/:filter?', component: Attendance, scopes: 'attendances.view' },
    { path: '/staff', component: People, scopes: 'users.*' },
    { path: '/staff/:user/:tab?', component: ViewPerson, scopes: 'users.*' },
    { path: '/time/:date?', component: Time, scopes: 'time.*' },
    { path: '/customers', component: Customers, scopes: 'customers.*' },
    { path: '/customers/:customer/:tab?', component: ViewCustomer, scopes: 'customers.*' },
    { path: '/vehicles', component: Vehicles, scopes: 'vehicles.*' },
    { path: '/estimates', component: Proposals, scopes: 'proposals.*' },
    { path: '/estimates/:proposal', component: ViewProposal, scopes: 'proposals.*' },
    { path: '/templates/:proposal', component: EditTemplate, scopes: 'boms.*' },
    { path: '/templates/tab/:tab?', component: Templates, scopes: 'boms.*' },
    { path: '/templates', component: Templates, scopes: 'boms.*' },
    { path: '/jobs/list', component: Projects, scopes: 'projects.*' },
    { path: '/jobs/dashboard', component: ProjectDashboard, scopes: ['projects.*', 'tasks.*'] },
    { path: '/jobs/dashboard/new-car', component: ProjectDashboardNewCar, scopes: ['projects.*', 'tasks.*'] },
    { path: '/jobs/current-projects', component: CurrentProjectsReport, scopes: 'projects.*' },
    { path: '/jobs/:project/:tab?', component: ViewProject, scopes: 'projects.*' },
    { path: '/suppliers', component: Suppliers, scopes: 'suppliers.*' },
    { path: '/suppliers/:supplier/:tab?', component: ViewSupplier, scopes: 'suppliers.*' },
    { path: '/inventory/parts', component: Parts, scopes: 'parts.*' },
    { path: '/inventory/parts/:part/:tab?', component: ViewPart, scopes: 'parts.*' },
    { path: '/inventory/purchase-orders', component: PurchaseOrders, scopes: 'purchase-orders.*' },
    { path: '/inventory/purchase-orders/:order', component: ViewOrder, scopes: 'purchase-orders.*' },
    { path: '/inventory/holding-orders', component: HoldingOrders, scopes: 'holding-orders.*' },
    { path: '/parts-picker', component: PartsPicker, scopes: 'parts-picker.*' },
    { path: '/invoices', component: Invoices, scopes: 'invoices.*' },
    { path: '/invoices/:invoice/:completed?', component: ViewInvoice, scopes: 'invoices.*' },
    { path: '/credits', component: Credits, scopes: 'credits.*' },
    { path: '/credits/:credit', component: ViewCredit, scopes: 'credits.*' },

    { path: '/downloads', component: Downloads, scopes: 'reports.*' },
    { path: '/reports', component: Reports, scopes: 'reports.*' },
    { path: '/reports/custom', component: CustomReports, scopes: 'reports.*' },

    { path: '/documents/:type', component: Documents, scopes: false, },
    { path: '/documents/:type/:document', component: DocumentsView, scopes: false, },

    { path: '/scheduling', component: Scheduling, scopes: 'scheduling.*' },

    { path: '/to-do', component: ToDo, scopes: false },

    { path: '/logs', component: Logs, scopes: 'logs.*' },

    { path: '/logout', component: Logout, scopes: false },

    { path: '/guest-checkin', component: GuestCheckin, scopes: false },

    { path: '/handbook', component: Handbook, scopes: 'handbook.*' },

    { path: '/overtime-approvals', component: OvertimeApprovals, scopes: 'overtime.manage' },

    { path: '/accident-log', component: AccidentLog, scopes: 'accident-log.*' },
    { path: '/accident-log/:accident_log/:tab?', component: AccidentLogEdit, scopes: 'accident-log.*' },

    { path: '/inventory/suspended-stock', component: SuspendedStock, scopes: 'suspended-stock.*' },

    { path: '/schedules', component: Schedules, scopes: 'schedules.*' },
    { path: '/schedules/:schedule', component: ViewSchedule, scopes: 'schedules.*', },

    { path: '/org-charts', component: OrgCharts, scopes: 'org-charts.*' },
    { path: '/org-charts/:chart', component: OrgChartsView, scopes: 'org-charts.*', },

    { path: '/below-zero/diary', component: BelowZeroDiary, scopes: 'below-zero.*' },

    { path: '/transactions', component: Transactions, scopes: ['transactions.view'] },

    { path: '/admin/deleted-items',  component: DeleteItems, scopes: ['deleted-items.manage'] },

    { path: '/admin/job-spec', component: JobSpec, scopes: 'job-spec.*' },

];

export default routes;
